import { create } from "zustand";

/**
 * Per-upload control handle for an in-flight chunked upload, keyed by uploadId
 * (which equals the attachment id). Holds:
 *  - `cancel`: aborts the upload's AbortController (the send flow then tears the
 *    upload down — deletes the message, reclaims the parts, removes it locally).
 *  - `file`: the original File, so the sender can preview the video locally while
 *    it's still uploading (the File is disk-backed, so keeping the reference is
 *    cheap — no heap copy).
 *
 * Entries are transient: registered when the upload starts and removed when it
 * settles (done / failed / cancelled). Kept separate from `UploadProgressState`
 * (which owns the 0–100 %) so the two concerns don't tangle.
 */
type UploadControl = {
  cancel: () => void;
  file: Blob;
  /** Releases the cross-tab Web Locks upload slot held by this session. */
  releaseSlot?: () => void;
};

type UploadControlState = {
  controls: Record<string, UploadControl>;
  register: (uploadId: string, control: UploadControl) => void;
  /** Trigger the registered cancel. No-op if the entry is already gone (race-safe). */
  cancel: (uploadId: string) => void;
  unregister: (uploadId: string) => void;
};

export const useUploadControlState = create<UploadControlState>()(
  (set, get) => ({
    controls: {},
    register(uploadId, control) {
      set((state) => ({
        controls: { ...state.controls, [uploadId]: control },
      }));
    },
    cancel(uploadId) {
      get().controls[uploadId]?.cancel();
    },
    unregister(uploadId) {
      // Free the cross-tab slot the moment the session settles — unregister
      // is already called on every settle path (complete / fail / cancel).
      get().controls[uploadId]?.releaseSlot?.();
      set((state) => {
        if (!(uploadId in state.controls)) {
          return state;
        }
        const next = { ...state.controls };
        delete next[uploadId];
        return { controls: next };
      });
    },
  }),
);

/**
 * Number of active chunked upload sessions this tab is tracking. The registry
 * is exactly "active chunked sessions": entries register when an upload starts
 * and unregister when it settles (complete / fail / cancel). Used as the
 * per-tab fallback count when Web Locks (cross-tab) is unavailable; the caller
 * owns the cap comparison.
 */
export function chunkedSessionCount(): number {
  return Object.keys(useUploadControlState.getState().controls).length;
}
